import championData from "constants/champions/championData.json";

// ---------------------------------------------------------------------------
// Champion image resolution for the /pro section.
// Primary source: the project's GCS bucket (fast, controlled).
// Fallback: CommunityDragon `latest` (always up to date) so a brand-new
// champion renders correctly even before the bucket is refreshed.
// Backend names ("Kai'Sa", "Nunu & Willump", "Wukong"...) are mapped to
// DDragon ids ("Kaisa", "Nunu", "MonkeyKing") by normalized name.
// ---------------------------------------------------------------------------

type ChampionEntry = { id: string; name: string; key: string };

const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, "");

const byNormName: Record<string, ChampionEntry> = {};
Object.values(championData as unknown as Record<string, ChampionEntry>).forEach(
  (c) => {
    if (!c?.id || c.id === "NoBan") return;
    byNormName[norm(c.name)] = c;
    byNormName[norm(c.id)] = c;
  },
);
// Common short aliases seen in esports data
const EXTRA_ALIASES: Record<string, string> = {
  nunu: "Nunu & Willump",
  wukong: "MonkeyKing",
  monkeyking: "MonkeyKing",
  renata: "Renata Glasc",
};
Object.entries(EXTRA_ALIASES).forEach(([alias, target]) => {
  const entry = byNormName[norm(target)];
  if (entry && !byNormName[alias]) byNormName[alias] = entry;
});

export function championId(name: string): string | null {
  return byNormName[norm(name)]?.id ?? null;
}

export function championDisplayName(name: string): string {
  return byNormName[norm(name)]?.name ?? name;
}

export function allChampionNames(): string[] {
  const seen = new Set<string>();
  const names: string[] = [];
  Object.values(byNormName).forEach((c) => {
    if (!seen.has(c.id)) {
      seen.add(c.id);
      names.push(c.name);
    }
  });
  return names.sort((a, b) => a.localeCompare(b));
}

const GCS = "https://storage.googleapis.com/drafter-champion-images/champions";
const CDRAGON = "https://cdn.communitydragon.org/latest/champion";

export function splashUrl(name: string): string | null {
  const id = championId(name);
  return id ? `${GCS}/splash/${id}.jpg` : null;
}

export function splashFallbackUrl(name: string): string | null {
  const id = championId(name);
  return id ? `${CDRAGON}/${id}/splash-art/centered` : null;
}

export function iconUrl(name: string): string | null {
  const id = championId(name);
  return id ? `${GCS}/icons/${id}.png` : null;
}

export function iconFallbackUrl(name: string): string | null {
  const id = championId(name);
  return id ? `${CDRAGON}/${id}/square` : null;
}

/** onError handler that swaps a GCS image for its CommunityDragon fallback. */
export function swapToFallback(
  e: React.SyntheticEvent<HTMLImageElement>,
  fallback: string | null,
): void {
  const img = e.currentTarget;
  if (fallback && img.src !== fallback) {
    img.src = fallback;
  } else {
    img.style.visibility = "hidden";
  }
}
